import React, { Suspense, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Button, Loader, ModalIsland } from '@saviynt/design-system';

import { useInvertedLocalize, useNavigateTo } from '../../shared/hooks';

import { ReviewState } from './data-layer/auditLogTypes';
import { useApiCancellation } from './hooks/useApiCancellation';
import {
  getRequestReviewDetailsErrorDisplay,
  type RequestReviewDetailsErrorDisplay,
} from './utils/getRequestReviewDetailsErrorDisplay';
import { navigateBackToSource } from './utils/utils';
import AuditLogMessages from './AuditLogMessages';
import AuditLogViewer from './AuditLogViewer';
import { useGetRequestReviewDetailsQuery } from './data-layer';
import { auditLogActions } from './data-store';
import type { AuditLogViewerContainerProps } from './types';

import './AuditLogViewerContainer.css';

// Phase 1: Lazy load AuditLogViewerAuditor - only loaded when isReadOnly is true
const AuditLogViewerAuditor = React.lazy(() =>
  (
    import('./AuditLogViewerAuditor/index.js') as Promise<{
      AuditLogViewerAuditor: React.ComponentType<
        Partial<AuditLogViewerContainerProps>
      >;
    }>
  ).then((module) => ({
    default: module.AuditLogViewerAuditor,
  }))
);

export const defaultProps: Partial<AuditLogViewerContainerProps> = {
  className: undefined,
};

/** Path to redirect users when Activity Log feature is disabled. */
const REQUEST_HOME_PATH = '/request/requestHome';

/**
 * AuditLogViewerContainer Component
 *
 * Container component that determines which audit log viewer to render
 * based on user role/permissions. This component acts as a smart wrapper
 * that handles the view switching logic.
 *
 * Views:
 * - Regular AuditLogViewer: For standard users
 * - AuditLogViewerAuditor: For auditor users with special permissions
 */
function AuditLogViewerContainer({
  className,
}: AuditLogViewerContainerProps): React.ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();
  const { navigateToUrl } = useNavigateTo();
  const { cancelAllRequests } = useApiCancellation();
  const { requestAccessKey: requestAccessKeyFromPath } = useParams<{
    requestAccessKey?: string;
  }>();
  const requestAccessKeyNum = Number(requestAccessKeyFromPath);

  const { data, isError, isLoading, error } = useGetRequestReviewDetailsQuery(
    { requestAccessKey: requestAccessKeyNum },
    { skip: !requestAccessKeyFromPath || Number.isNaN(requestAccessKeyNum) }
  );

  // RTK Query automatically cancels queries when:
  // 1. Component unmounts
  // 2. Query parameters change (requestAccessKey changes)
  // 3. Query is skipped
  // No manual abort needed for useQuery hooks

  // Cancel all requests on unmount
  useEffect(
    () => () => {
      cancelAllRequests();
    },
    [cancelAllRequests]
  );

  // Persist request review details when available
  useEffect(() => {
    if (data) {
      dispatch(
        auditLogActions.setRequestReviewDetails({
          requestMetadata: data.requestMetadata,
          isReadOnly: data.isReadOnly,
          reviewSummary: data.reviewSummary,
          state: data.state,
          launchDetail: data.launchDetail,
          exportMaxDateRangeMonths: data.exportMaxDateRangeMonths,
        })
      );
    }
  }, [data, dispatch]);

  // isActivityLogEnabled: false takes precedence - show blocking overlay on viewer
  const showActivityLogDisabledModal = data?.isActivityLogEnabled === false;

  // Handle ACCESS_DENIED state (only when isActivityLogEnabled is not false)
  if (
    !showActivityLogDisabledModal &&
    data?.state === ReviewState.ACCESS_DENIED
  ) {
    window.location.pathname = 'ECM/login/deniednew';

    return <div />;
  }

  // Handle INGESTION_IN_PROGRESS state
  if (
    !showActivityLogDisabledModal &&
    data?.state === ReviewState.INGESTION_IN_PROGRESS
  ) {
    return (
      <AuditLogMessages
        title={l('Logs Are Being Processed')}
        subtitle={l(
          'Activity logs will be available once processing is complete. Please refresh the page in a few minutes.'
        )}
        onClick={navigateBackToSource}
      />
    );
  }

  // Handle INGESTION_NOT_STARTED state
  if (
    !showActivityLogDisabledModal &&
    data?.state === ReviewState.INGESTION_NOT_STARTED
  ) {
    return (
      <AuditLogMessages
        title={l('Log Ingestion Not Started')}
        subtitle={l(
          'Activity logs are not yet available. Ingestion has not started for this request. Please check back later or contact your administrator.'
        )}
        onClick={navigateBackToSource}
      />
    );
  }

  if (!showActivityLogDisabledModal && isError) {
    const errorView: RequestReviewDetailsErrorDisplay =
      getRequestReviewDetailsErrorDisplay(error, l);

    return (
      <AuditLogMessages
        title={errorView.title}
        subtitle={errorView.subtitle}
        buttonText={l('Refresh')}
        onClick={() => window.location.reload()}
      />
    );
  }

  if (isLoading || !data) {
    return (
      <Box className='Aag-AuditLogViewerContainer-loader'>
        <Loader kind='loop' format='main' color='brand' />
      </Box>
    );
  }

  // View switching logic based on API response (no flicker)
  const viewerContent = data.isReadOnly ? (
    <Suspense
      fallback={
        <Box className='Aag-AuditLogViewerContainer-loader'>
          <Loader kind='loop' format='main' color='brand' />
        </Box>
      }>
      <AuditLogViewerAuditor className={className} />
    </Suspense>
  ) : (
    <AuditLogViewer />
  );

  return (
    <>
      {viewerContent}
      {showActivityLogDisabledModal && (
        <ModalIsland
          kind='critical'
          alertTitle={l('Feature Disabled')}
          alertSubtitle={l(
            'The Activity Log Viewer feature is currently disabled. Please contact your administrator.'
          )}
          onClose={() => {}}
          isCloseButtonVisible={false}
          isOpen
          overlayBackgroundColor='var(--color-neutral-900)'
          primaryButton={
            <Button
              type='button'
              kind='filled'
              size='medium'
              onClick={() => navigateToUrl(REQUEST_HOME_PATH)}>
              {l('Go to Home Page')}
            </Button>
          }
          className='Aag-AuditLogViewerContainer-activityLogDisabledModal'
        />
      )}
    </>
  );
}

AuditLogViewerContainer.defaultProps = defaultProps;

export default AuditLogViewerContainer;
